<template>
    <div v-if="!images" v-skeleton="images" class="image-swiper">
        <!--
            Not using skeleton on swiper directly since swiper doesn't work correctly with async children + loop.
            Images watcher + swiper.update() don't help
        -->
    </div>
    <Swiper v-else :options="swiperOptions" class="image-swiper" @slideChange="onSlideChange" keep-on-destroy>
        <div
            v-for="(image, index) in images"
            :key="'image' + index"
            class="swiper-slide"
            :class="{ 'swiper-no-swiping': images.length <= 1 }"
        >
            <ImageFluid
                :src="image['large']"
                :alt="image.altText"
                :animate-lazy="false"
                class="product-image"
                width="750"
                height="550"
            />
        </div>

        <template #after>
            <transition name="fade">
                <div v-if="notClicked && images.length > 1" class="image-swiper__help-wrapper">
                    <IconSvg filepath="gallery/swipe_left.svg" class="image-swiper__help-icon-left" />
                    <IconSvg filepath="gallery/touch_app.svg" />
                    <IconSvg filepath="gallery/swipe_right.svg" class="image-swiper__help-icon-right" />
                </div>
            </transition>
        </template>
    </Swiper>
</template>

<script>
import skeleton from '@/directives/skeleton';
import ImageFluid from '@/components/ImageFluid';

export default {
    name: 'ImageSwiper',
    directives: {
        skeleton
    },
    components: {
        ImageFluid,
        Swiper: () => import(/* webpackChunkName: "swiper" */ '@/components/swiper/Swiper')
    },
    data() {
        return {
            swiperOptions: {
                slidesPerView: 1,
                loop: true,
                on: {
                    click(swiper) {
                        swiper.slideNext();
                    }
                }
            },
            landscapeOrientationCheck: null,
            isLandscape: null,
            swiperIndex: 0,
            notClicked: true
        };
    },
    props: {
        images: Array
    },
    methods: {
        processOrientation(e) {
            this.isLandscape = e.matches;
        },
        onSlideChange(swiper) {
            this.notClicked = false;
            this.swiperIndex = swiper.realIndex;
        }
    },
    created() {
        if (process.client) {
            this.landscapeOrientationCheck = window.matchMedia('(orientation: landscape)');
            this.landscapeOrientationCheck.addListener(this.processOrientation);
            this.isLandscape = this.landscapeOrientationCheck.matches;
        }
    },
    beforeDestroy() {
        this.landscapeOrientationCheck.removeListener(this.processOrientation);
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';

.image-swiper {
    min-height: 200px;
    padding-top: variables.$image-aspect-ratio;
    position: relative;

    @media (orientation: landscape) {
        height: 100vh;
        padding-top: initial;
    }

    //ssr image shrinking prevention before swiper init
    .swiper-slide {
        flex-shrink: 0;
        width: 100%;
        height: 100%;
        position: relative;
    }

    ::v-deep .swiper-wrapper {
        position: absolute;
        top: 0;
        bottom: 0;
    }

    .product-image {
        min-height: 200px;
    }

    &__help-wrapper {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: 5px;
        z-index: 1;
        font-size: 2em;
        color: hsl(0, 0%, 100%);
        opacity: 0.9;
    }

    &__help-icon-left,
    &__help-icon-right {
        transform: translateX(0);
        animation-duration: 2s;
        animation-iteration-count: infinite;
    }

    &__help-icon-left {
        animation-name: swipe-left;
    }

    &__help-icon-right {
        animation-name: swipe-right;
    }

    .fade-leave-active {
        transition: opacity 0.5s;
    }

    .fade-leave-to {
        opacity: 0;
    }

    @keyframes swipe-left {
        100% {
            transform: translateX(-30%);
            opacity: 0;
        }
    }

    @keyframes swipe-right {
        100% {
            transform: translateX(30%);
            opacity: 0;
        }
    }
}
</style>
